import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { UserPlus, Link2, BarChart3, Clock, ArrowRight, CheckCircle2 } from 'lucide-react';

const STEPS = [
  {
    key: 'register',
    icon: UserPlus,
    time: '30 segundos',
    color: 'bg-brand-500',
    lightColor: 'bg-brand-50 dark:bg-brand-500/10',
    features: ['Sin tarjeta de crédito', 'Email o Google', 'Setup guiado'],
  },
  {
    key: 'connect',
    icon: Link2,
    time: '2 minutos',
    color: 'bg-accent-500',
    lightColor: 'bg-accent-50 dark:bg-accent-500/10',
    features: ['Agrega ingresos', 'Registra gastos', 'Conecta con pareja'],
  },
  {
    key: 'control',
    icon: BarChart3,
    time: 'Control inmediato',
    color: 'bg-emerald-500',
    lightColor: 'bg-emerald-50 dark:bg-emerald-500/10',
    features: ['Dashboard completo', 'Gráficas en tiempo real', 'Alertas personalizadas'],
  },
];

function StepVisual({ stepKey }) {
  const visuals = {
    register: (
      <div className="p-4">
        <div className="space-y-2">
          <div className="h-8 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center px-3">
            <span className="text-[10px] text-gray-400">tu@email.com</span>
          </div>
          <div className="h-8 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center px-3">
            <span className="text-[10px] text-gray-400">••••••••</span>
          </div>
          <div className="h-8 bg-brand-500 rounded-lg flex items-center justify-center">
            <span className="text-[10px] text-white font-semibold">Crear cuenta</span>
          </div>
        </div>
        <div className="flex items-center justify-center gap-2 mt-3">
          <div className="w-6 h-6 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center text-[10px]">G</div>
          <span className="text-[9px] text-gray-400">o continúa con Google</span>
        </div>
      </div>
    ),
    connect: (
      <div className="p-4">
        <div className="space-y-2">
          <div className="flex items-center gap-2 p-2 bg-emerald-50 dark:bg-emerald-500/10 rounded-lg">
            <span className="text-sm">💰</span>
            <div>
              <div className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-400">Salario</div>
              <div className="text-[9px] text-emerald-600/70">+$3,500,000</div>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-500/10 rounded-lg">
            <span className="text-sm">🍽️</span>
            <div>
              <div className="text-[10px] font-semibold text-red-700 dark:text-red-400">Supermercado</div>
              <div className="text-[9px] text-red-600/70">-$85,500</div>
            </div>
          </div>
          <div className="flex items-center justify-center gap-1 text-[9px] text-gray-400">
            <span>+ Agregar más</span>
          </div>
        </div>
      </div>
    ),
    control: (
      <div className="p-4">
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-2 border border-gray-100 dark:border-gray-700">
            <div className="text-[9px] text-gray-400">Balance</div>
            <div className="text-sm font-bold text-emerald-500">+$2.9M</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-2 border border-gray-100 dark:border-gray-700">
            <div className="text-[9px] text-gray-400">Gastos</div>
            <div className="text-sm font-bold text-red-400">-$1.4M</div>
          </div>
        </div>
        <div className="h-12 flex items-end gap-1">
          {[30, 45, 35, 60, 50, 70, 55].map((h, i) => (
            <div key={i} className="flex-1 bg-brand-400/60 dark:bg-brand-500/40 rounded-sm" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
    ),
  };

  return visuals[stepKey] || null;
}

export default function HowItWorks2() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef();

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="how-it-works" className="py-20 px-4 bg-gray-50 dark:bg-gray-900/50">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block px-3 py-1 rounded-full bg-brand-100 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 text-xs font-semibold mb-4">
            EMPIEZA EN MINUTOS
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-gray-900 dark:text-white mb-4">
            ¿Cómo funciona?
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-xl mx-auto">
            Tres pasos simples para tomar el control de tus finanzas. Sin complicaciones.
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connector line - desktop */}
          <div className="hidden md:block absolute top-24 left-[16.67%] right-[16.67%] h-0.5 bg-gray-200 dark:bg-gray-800">
            <div 
              className="h-full bg-gradient-to-r from-brand-500 via-accent-500 to-emerald-500 transition-all duration-1000"
              style={{ width: isVisible ? '100%' : '0%' }}
            />
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              const isActive = activeStep === i;

              return (
                <div
                  key={step.key}
                  className={`relative transition-all duration-700 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${i * 200}ms` }}
                  onMouseEnter={() => setActiveStep(i)}
                >
                  {/* Step number bubble */}
                  <div className="relative z-10 flex justify-center mb-6">
                    <div 
                      className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                        isActive ? step.lightColor : 'bg-white dark:bg-gray-800'
                      } border-2 ${isActive ? `border-${step.color.split('-')[1]}-500` : 'border-gray-200 dark:border-gray-700'}`}
                    >
                      <Icon size={20} className={isActive ? step.color.replace('bg-', 'text-') : 'text-gray-400'} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="text-center">
                    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold mb-2 ${step.lightColor} ${step.color.replace('bg-', 'text-')}`}>
                      <Clock size={10} />
                      {step.time}
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      {t(`landing.howItWorks.steps.${step.key}.title`)}
                    </h3>
                    
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      {t(`landing.howItWorks.steps.${step.key}.description`)}
                    </p>

                    {/* Features */}
                    <ul className="space-y-1 mb-4">
                      {step.features.map((feat, fi) => (
                        <li key={fi} className="flex items-center justify-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                          <CheckCircle2 size={12} className="text-emerald-500" />
                          {feat}
                        </li>
                      ))}
                    </ul>

                    {/* Visual mockup */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
                      <StepVisual stepKey={step.key} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-14">
          <a href="#pricing" className="inline-flex items-center gap-2 text-brand-600 dark:text-brand-400 font-semibold hover:underline">
            Ver planes y precios
            <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}
